import { MdOutlineNotificationsNone } from "react-icons/md";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { getMessagingInstance } from "./firebase";
import { getToken, onMessage } from "firebase/messaging";
import {
  getNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  saveFcmToken,
} from "./serverComm";
import { getTimeAgo } from "./utils/helper";
import Button from "./components/Button/Button";
import { useNavigate } from "react-router-dom";
import debounce from "lodash/debounce";
import { AppLoader } from "./components/AppLoader";
import { sanitizeRichText } from "./utils/sanitizeHtml";

const sanitizeHtml = (html) => {
  if (!html) return '';
  return sanitizeRichText(html);
};

const Notification = ({ openUpgrade }) => {
  const navigate = useNavigate();
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false);
  const [notificationList, setNotificationList] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const notificationsRef = useRef(null);

  const fetchNotifications = useCallback(
    debounce(async (page) => {
      setLoading(true);
      try {
        const data = await getNotifications(page);

        // Filter notifications to show only from last 7 days
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

        const filteredList = (data.list || []).filter((notification) => {
          const notificationDate = new Date(notification.createdAt);
          return notificationDate >= sevenDaysAgo;
        });

        const hasAnyUnreadNotification = filteredList.find(
          (n) => n.isRead === false
        );
        if (hasAnyUnreadNotification) {
          setHasUnreadNotifications(true);
        }
        // If it's the first page, replace the list; otherwise append
        if (page === 1) {
          setNotificationList(filteredList);
        } else {
          setNotificationList((prev) => {
            // Filter the existing list as well to ensure consistency
            const filteredPrev = prev.filter((notification) => {
              const notificationDate = new Date(notification.createdAt);
              return notificationDate >= sevenDaysAgo;
            });
            return [...filteredPrev, ...filteredList];
          });
        }

        setHasMore(data.hasMore && filteredList.length > 0);
      } catch (error) {
        console.warn("Failed to load notifications:", error);
        if (page === 1) {
          setNotificationList([]);
        }
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    }, 1000),
    []
  );

  const markAllAsReadAndOpen = async () => {
    if (hasUnreadNotifications) {
      try {
        await markAllNotificationsRead();
        // Update local state to mark all notifications as read
        setNotificationList((prev) =>
          prev.map((n) => ({ ...n, isRead: true }))
        );
        setHasUnreadNotifications(false);
      } catch (error) {
        console.error("Error marking all notifications as read:", error);
      }
    }
  };

  const handleNotificationClick = async (data) => {
    await markNotificationRead(data._id);
    let isAnyUnreadRemaining = false;
    // making notification status read
    setNotificationList((prev) =>
      prev.map((n) => {
        if (n._id === data._id) {
          return { ...n, isRead: true };
        } else {
          if (n.isRead === false) {
            isAnyUnreadRemaining = true;
          }
          return n;
        }
      })
    );
    if (!isAnyUnreadRemaining) {
      setHasUnreadNotifications(false);
    }
    switch (data.template) {
      case "FIRST_SURVEY_CREATED":
        navigate(`/survey/${data.surveyId}?modal=share`);
        break;
      case "SURVEY_IS_LIVE":
        // some logic
        break;
      case "FIRST_RESPONSE_ON_SURVEY":
        navigate(`/survey/${data.surveyId}/analytics`);
        break;
      case "TEN_RESPONSES_ON_SURVEY":
        // some logic
        break;
      case "HUNDRED_RESPONSES_ON_SURVEY":
        // some logic
        break;
      case "SHARED_TEN_TIMES":
        // some logic
        break;
      case "USED_LOGIC":
        // some logic
        break;
      case "RESPONSES_REACHED_FREE_LIMIT":
        openUpgrade();
        break;
      case "UNLOCKED_PRO":
        // some logic
        break;
      case "RESPONSES_REACHED_PRO_LIMIT":
        openUpgrade();
        break;
      case "UNLOCKED_GROWTH":
        // some logic
        break;
      case "SURVEY_STOPPED_DUE_TO_LIMIT":
        openUpgrade();
        break;
      case "LOTS_OF_RESPONSES":
        // some logic
        break;
      case "SURVEY_CLOSED":
        // some logic
        break;
      default:
        // code block for value1
        break;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        notificationsRef.current &&
        !notificationsRef.current.contains(event.target)
      ) {
        setIsNotificationsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    let unsubscribe;
    
    const initializeMessaging = async () => {
      try {
        const messaging = await getMessagingInstance();
        if (!messaging) {
          console.warn('Firebase Messaging not available in this browser');
          return;
        }

        // Request permission and store token
        try {
          const currentToken = await getToken(messaging, {
            vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
          });
          await saveFcmToken(currentToken);
        } catch (error) {
          console.error("Token error:", error);
        }

        // Set up message listener
        unsubscribe = onMessage(messaging, (payload) => {
          setNotificationList((prev) => [
            { ...payload.notification, ...payload.data, isRead: false },
            ...prev,
          ]);
          setHasUnreadNotifications(true);
        });
      } catch (error) {
        console.error("Messaging initialization error:", error);
      }
    };

    initializeMessaging();
    
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  useEffect(() => {
    if (hasMore) {
      if (page === 1) {
        setNotificationList([]);
      }
      fetchNotifications(page);
    }
  }, [page]);

  return (
    <div className="relative" ref={notificationsRef}>
      <button
        onClick={async () => {
          if (notificationList.length <= 0 && !isNotificationsOpen) {
            return;
          }
          
          // Toggle the notification panel
          const willOpen = !isNotificationsOpen;
          setIsNotificationsOpen(willOpen);
          
          // If opening the panel and there are unread notifications, mark them as read
          if (willOpen && hasUnreadNotifications) {
            await markAllAsReadAndOpen();
          }
        }}
        className="relative"
      >
        <div className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors">
          <MdOutlineNotificationsNone size={18} className="text-gray-600" />
        </div>
        {hasUnreadNotifications && (
          <span className="absolute top-0 right-0 w-2.5 h-2.5 bg-red-500 rounded-full" />
        )}
      </button>

      {isNotificationsOpen && (
        <div className="absolute right-0 mt-2 w-72 sm:w-96 bg-white rounded-xl shadow-lg border border-gray-100 z-50 
                        max-w-[calc(100vw-2rem)] sm:max-w-none
                        -translate-x-2 sm:translate-x-0">
          <div className="px-3 sm:px-4 py-2.5 border-b border-gray-100">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">Notifications</h3>
          </div>

          <div className="max-h-[400px] overflow-y-auto py-1">
            <div>
              {notificationList.map((notif) => {
                return (
                  <NotificationItem
                    key={notif._id}
                    data={notif}
                    handleNotificationClick={handleNotificationClick}
                  />
                );
              })}
              {hasMore && (
                <div className="text-center text-sm p-3 flex justify-center items-center">
                  <span
                    onClick={() => setPage((prev) => prev + 1)}
                    className="cursor-pointer hover:text-blue-700 font-medium"
                  >
                    {loading ? <AppLoader size={4} /> : "Load previous"}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Notification;

const NotificationItem = ({ data, handleNotificationClick }) => {
  const [copyStatus, setCopyStatus] = useState("Copy Link");
  const isNew = data.isRead === false;
  const isLimitReachedAlert = [
    "RESPONSES_REACHED_FREE_LIMIT",
    "RESPONSES_REACHED_PRO_LIMIT",
    "SURVEY_STOPPED_DUE_TO_LIMIT",
  ].includes(data.template);

  const hasBtn = [
    "RESPONSES_REACHED_FREE_LIMIT",
    "RESPONSES_REACHED_PRO_LIMIT",
    "SURVEY_STOPPED_DUE_TO_LIMIT",
    "FIRST_SURVEY_CREATED",
    "FIRST_RESPONSE_ON_SURVEY",
    "SURVEY_IS_LIVE",
  ].includes(data.template);

  const handleCopyLink = async () => {
    const surveyUrl = `${window.location.origin}/s/${data.surveyId}`;
    try {
      await navigator.clipboard.writeText(surveyUrl);
      setCopyStatus("copied");
      setTimeout(() => {
        setCopyStatus("Copy Link");
        handleNotificationClick(data);
      }, 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <div
      className={`px-3 sm:px-4 py-3 hover:bg-gray-50 rounded-lg mx-2 my-0.5 transition-colors duration-200 ${
        hasBtn ? "" : "cursor-pointer"
      } ${isLimitReachedAlert ? "bg-[#FEF1F1]" : isNew ? "bg-[#FFFAE9]" : ""}`}
    >
      <div
        className="flex justify-between items-center gap-3"
        onClick={() => {
          if (!hasBtn) {
            handleNotificationClick(data);
          }
        }}
      >
        <div className="flex-1">
          <p
            className="text-sm sm:text-base text-app-black mt-0.5 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(data.body) }}
          />
          <p className="text-xs sm:text-sm text-gray-500 mt-1.5">
            {getTimeAgo(data.createdAt)}
          </p>
        </div>
        {isNew && (
          <span className="inline-flex items-center px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium bg-[#F1F0D7] text-app-black">
            New
          </span>
        )}
      </div>
      {hasBtn && data.template === "FIRST_SURVEY_CREATED" && data.surveyId && (
        <Button
          className="px-3 py-1.5 text-sm mt-2 rounded-lg font-medium"
          onClick={() => handleNotificationClick(data)}
        >
          Share Survey
        </Button>
      )}
      {hasBtn &&
        data.template === "FIRST_RESPONSE_ON_SURVEY" &&
        data.surveyId && (
          <Button
            className="px-3 py-1.5 text-sm mt-2 rounded-lg font-medium"
            onClick={() => handleNotificationClick(data)}
          >
            View Response
          </Button>
        )}
      {hasBtn && data.template === "SURVEY_IS_LIVE" && data.surveyId && (
        <Button
          variant="outline"
          className="px-3 py-1.5 text-sm mt-2 rounded-lg font-medium"
          onClick={handleCopyLink}
          disabled={copyStatus === "copied"}
        >
          {copyStatus}
        </Button>
      )}
      {hasBtn && isLimitReachedAlert && (
        <Button
          className="px-3 py-1.5 text-sm mt-2 rounded-lg font-medium"
          onClick={() => handleNotificationClick(data)}
        >
          Upgrade
        </Button>
      )}
    </div>
  );
};
